/**
 * Budget Table Topic Row Component
 *
 * Middle hierarchy level row for budget topics in table view
 * Displays topic name, totals, and controls for expansion and adding line items
 *
 * Design specs from Figma:
 * - Height: 60px
 * - Padding: 20px horizontal
 * - Font: 16px bold white (slightly smaller than phase)
 * - Same colors as phases but slightly transparent
 * - Columns: checkbox (50px), name with indent (flex-1), 3x totals (160px each), chevron, "+ Add Linha"
 */

import { cn } from "@/lib/class-control";
import { Checkbox } from "@/components/ui/checkbox";
import { PhaseColor } from "@/types/orcamentos.interface";
import { Eye, Pencil, Trash2 } from "lucide-react";

interface BudgetTableTopicRowProps {
  /** Topic name to display */
  topicName: string;
  /** Hierarchical number (e.g., "1.1", "2.1") */
  topicNumber: string;
  /** Color identifier matching parent phase */
  phaseColor: PhaseColor;
  /** Whether topic is expanded */
  isExpanded: boolean;
  /** Toggle expansion callback */
  onToggle: () => void;
  /** Total executed budget */
  totalExecutado: number;
  /** Total approved budget */
  totalAprovado: number;
  /** Percentage executed */
  percentual: number;
  /** Checkbox checked state */
  checked: boolean;
  /** Checkbox change callback */
  onCheckChange: (checked: boolean) => void;
  /** View callback */
  onView?: () => void;
  /** Edit callback */
  onEdit?: () => void;
  /** Delete callback */
  onDelete?: () => void;
}

/**
 * Get background color with opacity based on phase color identifier
 */
function getTopicBackground(color: PhaseColor): string {
  const colorMap: Record<PhaseColor, string> = {
    purple: "rgba(120, 70, 255, 0.7)",
    teal: "rgba(19, 212, 128, 0.7)",
    orange: "rgba(255, 107, 74, 0.7)",
  };
  return colorMap[color];
}

/**
 * Format currency to Brazilian Real
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

export function BudgetTableTopicRow({
  topicName,
  topicNumber,
  phaseColor,
  isExpanded,
  onToggle,
  totalExecutado,
  totalAprovado,
  percentual,
  checked,
  onCheckChange,
  onView,
  onEdit,
  onDelete,
}: BudgetTableTopicRowProps) {
  return (
    <tr
      className="h-[60px] cursor-pointer"
      style={{ backgroundColor: getTopicBackground(phaseColor) }}
      onClick={onToggle}
    >
      {/* Checkbox column */}
      <td className="w-[50px] text-center" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-center">
          <Checkbox
            checked={checked}
            onCheckedChange={onCheckChange}
            className="border-white data-[state=checked]:bg-white data-[state=checked]:text-[#7846FF]"
          />
        </div>
      </td>

      {/* Topic name column with hierarchical number */}
      <td className="px-[20px] text-[14px] font-semibold text-white">
        <span className="mr-2">{topicNumber}</span>
        {topicName}
      </td>

      {/* Empty column for Última atualização */}
      <td className="w-[160px]" />

      {/* Total Orçamento Aprovado */}
      <td className="w-[180px] px-[16px] text-right text-[14px] font-semibold text-white">
        {formatCurrency(totalAprovado)}
      </td>

      {/* Total Orçamento Executado */}
      <td className="w-[180px] px-[16px] text-right text-[14px] font-semibold text-white">
        {formatCurrency(totalExecutado)}
      </td>

      {/* Percentual */}
      <td className="w-[100px] px-[16px] text-right text-[14px] font-semibold text-white">
        {percentual}%
      </td>

      {/* Actions */}
      <td className="w-[100px] px-[16px]">
        <div className="flex items-center justify-end gap-[8px]" onClick={(e) => e.stopPropagation()}>
          {onView && (
            <button
              onClick={onView}
              className="text-[#888888] hover:text-white transition-colors p-[4px]"
              aria-label="Visualizar"
              title="Visualizar"
            >
              <Eye size={20} />
            </button>
          )}

          {onEdit && (
            <button
              onClick={onEdit}
              className="text-[#888888] hover:text-white transition-colors p-[4px]"
              aria-label="Editar"
              title="Editar"
            >
              <Pencil size={20} />
            </button>
          )}

          {onDelete && (
            <button
              onClick={onDelete}
              className="text-[#888888] hover:text-[#DC4426] transition-colors p-[4px]"
              aria-label="Deletar"
              title="Deletar"
            >
              <Trash2 size={20} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
